import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  CheckoutOrderResponse,
  CheckoutPatientAddress,
  CheckoutPatientAttributes,
  CheckoutPatientData,
  CheckoutPaymentResponse
} from 'components/modules/patientArea/products/types'
import { filterDiscontinuedSexShopVariants } from 'constants/discontinuedSexShopVariants'
import { SexShopProduct } from 'constants/packageVariants'
import { Params } from 'next/dist/shared/lib/router/utils/route-matcher'
import { ShipmentData } from 'redux/slices/packages-slice'
import { createPatientData } from 'redux/thunks/create-patient-data'
import { createSpreeAddressId } from 'redux/thunks/create-spree-address-id'
import { fetchShipmentData } from 'redux/thunks/fetch-shipment-data'
import nestApiService from 'services/api/nest-api-service'
import { RecordOfAny } from 'types'
import { getCookies } from 'utils/cookieHelpers'
import { getOpenJwtToken } from 'utils/getOpenJwtToken'

interface CheckoutSexToolsState {
  isLoading: boolean
  spreeAddressId: number | null
  patientAttributes: CheckoutPatientAttributes | null
  patientAddress: CheckoutPatientAddress | null
  patientData: CheckoutPatientData | null
  orderVariants: SexShopProduct[]
  shipmentData: ShipmentData[] | null
  paymentResponse: CheckoutPaymentResponse | null
  orderResponse: CheckoutOrderResponse | null
  sexShopProducts: SexShopProduct[]
  chosenShipmentMethod: ShipmentData | null
  deliveryType: 'PAC' | 'SEDEX' | ''
  appliedCoupon: { total: number } | null
}

const { value } = getCookies('client')
const isSmartfit = value === 'smartfitnutri'

const initialState: CheckoutSexToolsState = {
  isLoading: false,
  spreeAddressId: null,
  patientAttributes: null,
  patientAddress: null,
  patientData: null,
  orderVariants: [],
  sexShopProducts: [],
  shipmentData: [],
  paymentResponse: null,
  orderResponse: null,
  chosenShipmentMethod: null,
  deliveryType: 'SEDEX',
  appliedCoupon: null
}

const checkoutSexToolsSlice = createSlice({
  name: 'checkout-sex-tools',
  initialState,
  reducers: {
    resetCheckoutSexToolsState: (state) => {
      state = initialState
      return state
    },
    setPatientAttributes: (state, { payload }: PayloadAction<CheckoutPatientAttributes | null>) => {
      state.patientAttributes = payload
    },
    setOrderVariants: (state, { payload }: PayloadAction<SexShopProduct[]>) => {
      state.orderVariants = payload
    },
    setDeliveryType: (state, { payload }: PayloadAction<'PAC' | 'SEDEX' | ''>) => {
      state.deliveryType = payload
    },
    setChosenShipmentMethod: (state, { payload }: PayloadAction<ShipmentData | null>) => {
      state.chosenShipmentMethod = payload
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(getSexShopProducts.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getSexShopProducts.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.sexShopProducts = filterDiscontinuedSexShopVariants(payload)
      })
      .addCase(getSexShopProducts.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(fetchShipmentData.pending, (state) => {
        state.isLoading = true
      })
      .addCase(fetchShipmentData.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.shipmentData = payload
      })
      .addCase(fetchShipmentData.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(createPatientData.pending, (state) => {
        state.isLoading = true
      })
      .addCase(createPatientData.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.patientData = payload
      })
      .addCase(createPatientData.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(createSpreeAddressId.pending, (state) => {
        state.isLoading = true
      })
      .addCase(createSpreeAddressId.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.spreeAddressId = payload
      })
      .addCase(createSpreeAddressId.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(applySexToolsCoupon.pending, (state) => {
        state.isLoading = true
      })
      .addCase(applySexToolsCoupon.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.appliedCoupon = payload
      })
      .addCase(applySexToolsCoupon.rejected, (state) => {
        state.isLoading = false
        state.appliedCoupon = null
      })
      .addCase(sexToolsOrderCheckout.pending, (state) => {
        state.isLoading = true
      })
      .addCase(sexToolsOrderCheckout.fulfilled, (state, { payload }) => {
        state.orderResponse = payload
        state.isLoading = false
      })
      .addCase(sexToolsOrderCheckout.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(sexToolsPaymentCheckout.pending, (state) => {
        state.isLoading = true
      })
      .addCase(sexToolsPaymentCheckout.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.paymentResponse = payload
      })
      .addCase(sexToolsPaymentCheckout.rejected, (state) => {
        state.isLoading = false
      })
})

export const {
  resetCheckoutSexToolsState,
  setPatientAttributes,
  setOrderVariants,
  setDeliveryType,
  setChosenShipmentMethod
} = checkoutSexToolsSlice.actions

export default checkoutSexToolsSlice.reducer

interface GetSexShopProductsParams {
  isSmartfit: boolean
}

export const getSexShopProducts = createAsyncThunk<SexShopProduct[], GetSexShopProductsParams>(
  'get-sex-shop-products',
  async ({ isSmartfit }, { rejectWithValue }) => {
    try {
      const token = await getOpenJwtToken()
      const sexshopEndpoint = isSmartfit ? '/products/sex-shop?isSmartFit=true' : '/products/sex-shop'

      const { data } = await nestApiService.apiInstance.get<SexShopProduct[]>(sexshopEndpoint, {
        headers: {
          Authorization: token
        }
      })

      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const sexToolsOrderCheckout = createAsyncThunk(
  'sex-tools-order-checkout',
  async (apiData: RecordOfAny, { rejectWithValue }) => {
    try {
      const token = await getOpenJwtToken()

      const { data } = await nestApiService.apiInstance.post('/shop/checkout/order', apiData, {
        headers: {
          Authorization: token
        }
      })

      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

interface SexToolsCheckoutParams {
  orderData: RecordOfAny
}

export const sexToolsPaymentCheckout = createAsyncThunk(
  'sex-tools-payment-checkout',
  async ({ orderData }: SexToolsCheckoutParams, { rejectWithValue }) => {
    try {
      const token = await getOpenJwtToken()

      const { data } = await nestApiService.apiInstance.post('/shop/checkout/payment', orderData, {
        headers: {
          Authorization: token
        }
      })

      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

interface ApplySexToolsCouponParams {
  coupon_code: string
  order_id: number
}

export const applySexToolsCoupon = createAsyncThunk(
  'apply-sex-tools-coupon',
  async ({ coupon_code, order_id }: ApplySexToolsCouponParams, { rejectWithValue }) => {
    try {
      const token = await getOpenJwtToken()

      const { data } = await nestApiService.apiInstance.post(
        '/shop/checkout/coupon',
        {
          coupon_code,
          order_id
        },
        {
          headers: {
            Authorization: token
          }
        }
      )

      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export type CheckoutSexToolsStep = 'info' | 'address' | 'pick-variant' | 'payment' | 'confirm'

export const SEX_TOOLS_CHECKOUT_PATHNAME = '/sex-tools-checkout/[step]'
export const SEX_TOOLS_CHECKOUT_INITIAL_STEP = `/sex-tools-checkout/info`
